/**
 * P1.5 — Worker payroll-setup completeness (prevention, advisory only).
 *
 * Pure detector: given already-loaded setup facts for an effective date,
 * reports whether a shift on that date would hit the pay-context resolver's
 * SETUP HARD blocks (apps/web pay-context-resolver.ts):
 *
 *   STAFF_REQUIRED                  — staffId present (caller responsibility)
 *   STAFF_CLASSIFICATION_NOT_FOUND  — no stream/level covering the date
 *   STAFF_PAY_PROFILE_NOT_FOUND     — no single active pay profile covering the date
 *   PAY_ROLE_REQUIRED               — active assignments exist but none usable
 *   PAY_ROLE_ASSIGNMENT_REQUIRED    — role path without a matching assignment
 *   PAY_ROLE_NOT_ACTIVE             — assigned tenant pay role is not ACTIVE
 *   SALARY_FLAT_CONFIG_INCOMPLETE   — annualised salary / cadence missing
 *   SALARY_FLAT_REVIEW_REQUIRED     — annualised-salary reconciliation review pending
 *
 * This module does not call the resolver, write data, or gate anything.
 * complete === true iff none of the mapped setup gaps fire for the facts.
 *
 * G-9: a MA000018 direct-care L4 without an authored I.1.1 / B.2.4 path is
 * incomplete. Retained-rate and cl 28.3 leave stay in
 * classification-historical-inputs.ts — they are not defaulted here.
 *
 * Callers that cannot read setup facts for the current role (RLS / column
 * grants) must NOT call this with empty defaults and treat the result as
 * complete or incomplete — render an UNKNOWN state instead.
 */

import { unresolvedClassificationHistoricalInputs } from './classification-historical-inputs';

export type PayrollSetupGapCode =
  | 'STAFF_CLASSIFICATION_NOT_FOUND'
  | 'PAY_POINT_MISSING'
  | 'STAFF_PAY_PROFILE_NOT_FOUND'
  | 'PAY_ROLE_REQUIRED'
  | 'PAY_ROLE_ASSIGNMENT_REQUIRED'
  | 'PAY_ROLE_NOT_ACTIVE'
  | 'AGREED_RATE_REQUIRED'
  | 'EMPLOYEE_CATEGORY_REQUIRED'
  | 'AWARD_REVIEW_REQUIRED'
  | 'PAY_ROLE_LINKS_INCOMPATIBLE'
  | 'EMPLOYMENT_TYPE_REQUIRED'
  | 'SALARY_FLAT_CONFIG_INCOMPLETE'
  | 'SALARY_FLAT_REVIEW_REQUIRED'
  | 'AGED_CARE_DC_L4_UNRESOLVED';

export interface PayrollSetupGap {
  /** Stable machine code (aligned with resolver HARD codes where applicable). */
  code: PayrollSetupGapCode;
  /** Plain-English title for provider vocabulary. */
  title: string;
  /** One-line detail: what to fix, no engine jargon. */
  detail: string;
}

export interface WorkerPayrollSetupSalaryFlatConfig {
  annualisedSalary: number | string | null;
  payPeriodsPerYear: number | null;
  /** WEEKLY | FORTNIGHTLY | MONTHLY — required and must match periods-per-year. */
  salaryPayFrequency: string | null;
}

/**
 * Pre-loaded, effective-dated facts for one worker on one date.
 * Callers resolve history/assignment coverage before calling.
 */
export interface WorkerPayrollSetupInput {
  /**
   * Payment method on staff (AWARD default when null/blank).
   * SALARY_FLAT uses the salary-flat path; AGENCY_INVOICE / VOLUNTEER are
   * not award-payroll setup and are treated as complete for this detector.
   */
  paymentMethod?: string | null;
  employmentType?: string | null;
  awardCode?: string | null;
  /**
   * Classification covering the effective date (stream + level required on
   * award path). Prefer the classification LINKED by the single active pay
   * profile (or pay-role assignment), not an independently selected newest row.
   */
  classificationStream?: string | null;
  classificationLevel?: string | null;
  payPoint?: string | null;
  /** Provider-confirmed rate on the effective linked classification. */
  agreedHourlyRate?: number | string | null;
  /** Provider-confirmed staff.custom_fields.employee_category evidence. */
  employeeCategory?: string | null;
  /** Material reclassification remains review-required until signed off. */
  needsAwardReview?: boolean | null;
  /** Dated assignment/profile/classification pins agree with the selected role. */
  payRoleLinksCompatible?: boolean | null;
  /**
   * True when exactly one ACTIVE staff_pay_profile_history row covers the
   * date (mirrors loadSingleActiveStaffProfile: count must be 1, not ≥1).
   */
  hasActivePayProfile: boolean;
  /**
   * When known: whether the classification facts above are those linked by
   * the single active profile (or active pay-role assignment).
   * - true  → linkage validated
   * - false → known not linked / multiple profiles / missing link id
   * - null/undefined → linkage not knowable at this layer; on award path
   *   without pay-role, do not claim complete on independent classification
   */
  classificationLinkedToActiveProfile?: boolean | null;
  /**
   * True when at least one ACTIVE staff_pay_role_assignment covers the date
   * (regardless of eligibility). When true, the resolver uses the pay-role
   * path rather than legacy profile-only resolution.
   */
  hasActivePayRoleAssignment: boolean;
  /**
   * True when an ACTIVE assignment covering the date has eligibility_status
   * ELIGIBLE and points at an ACTIVE tenant_pay_roles row.
   */
  hasActiveEligiblePayRoleAssignment: boolean;
  /**
   * When hasActivePayRoleAssignment is true: whether the best-known assigned
   * tenant pay role is ACTIVE. Null = unknown (do not emit PAY_ROLE_NOT_ACTIVE).
   */
  assignedPayRoleIsActive?: boolean | null;
  /** Salary-flat annualised config when paymentMethod is SALARY_FLAT. */
  salaryFlat?: WorkerPayrollSetupSalaryFlatConfig | null;
  /**
   * When paymentMethod is SALARY_FLAT: true if annualised-salary reconciliation
   * review is still required (resolver HARD SALARY_FLAT_REVIEW_REQUIRED).
   * Null/undefined = not provided (do not emit the gap).
   */
  salaryFlatReviewRequired?: boolean | null;
  /** Tenant-local YYYY-MM-DD used for the B.2.4 L4 reachability check. */
  asOfDate?: string | null;
  /**
   * How a MA000018 direct-care L4 was reached. Null/undefined on a DC L4
   * worker is a setup gap — current rows cannot tell I.1.1 from B.2.4.
   */
  agedCareDcL4Path?: 'TRANSLATED_I11' | 'B24_SERVICE' | null;
  /** Inclusive start of L3 used to count B.2.4 years. Pre-2025 over-classifies. */
  agedCareL3ClassifiedFrom?: string | null;
}

export interface WorkerPayrollSetupStatus {
  complete: boolean;
  gaps: PayrollSetupGap[];
}

const PAY_PERIODS_BY_FREQUENCY: Record<string, number> = {
  WEEKLY: 52,
  FORTNIGHTLY: 26,
  MONTHLY: 12,
};

function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalisedPaymentMethod(method: string | null | undefined): string {
  const raw = typeof method === 'string' ? method.trim().toUpperCase() : '';
  return raw.length > 0 ? raw : 'AWARD';
}

function isSalaryFlatMethod(method: string): boolean {
  return method === 'SALARY_FLAT';
}

/** Non-award payroll paths do not require classification / profile / pay-role setup. */
function isNonAwardPayrollPath(method: string): boolean {
  return method === 'AGENCY_INVOICE' || method === 'VOLUNTEER';
}

/**
 * Aligns with salary-flat normalise (salary-flat.ts normaliseSalaryFlatStaff):
 * annualised salary > 0, pay periods > 0, and a supported frequency that
 * matches periods-per-year. Frequency is required — missing frequency fails.
 */
export function isSalaryFlatConfigComplete(
  config: WorkerPayrollSetupSalaryFlatConfig | null | undefined,
): boolean {
  if (!config) return false;
  const annualised =
    config.annualisedSalary == null || config.annualisedSalary === ''
      ? null
      : Number(config.annualisedSalary);
  if (annualised == null || !Number.isFinite(annualised) || annualised <= 0) return false;
  const periods = config.payPeriodsPerYear;
  if (periods == null || !Number.isFinite(periods) || periods <= 0) return false;
  if (!hasText(config.salaryPayFrequency)) return false;
  const expected = PAY_PERIODS_BY_FREQUENCY[config.salaryPayFrequency!.trim().toUpperCase()];
  if (expected == null || expected !== periods) return false;
  return true;
}

function gap(code: PayrollSetupGapCode, title: string, detail: string): PayrollSetupGap {
  return { code, title, detail };
}

const GAP_CATALOGUE: Record<PayrollSetupGapCode, Omit<PayrollSetupGap, 'code'>> = {
  STAFF_CLASSIFICATION_NOT_FOUND: {
    title: 'No pay classification set',
    detail: "Set the worker's award classification and level so shifts can resolve pay context.",
  },
  PAY_POINT_MISSING: {
    title: 'No pay point set',
    detail:
      "Set the worker's pay point (for example the second part of a level like L3.2) for accurate rates.",
  },
  STAFF_PAY_PROFILE_NOT_FOUND: {
    title: 'No active pay profile for the current date',
    detail:
      'Assign an active pay profile covering today so payroll can match award, employment type, and EA rules. Exactly one active profile must cover the date.',
  },
  PAY_ROLE_REQUIRED: {
    title: 'No active pay role for the current date',
    detail:
      'This worker has pay-role setup in progress but no usable active pay role for today. Assign an active eligible pay role.',
  },
  PAY_ROLE_ASSIGNMENT_REQUIRED: {
    title: 'No eligible pay-role assignment for the current date',
    detail:
      'Assign an eligible pay role for this worker covering today, or clear incomplete role assignments so the profile path can be used.',
  },
  PAY_ROLE_NOT_ACTIVE: {
    title: 'Assigned pay role is not active',
    detail:
      'The worker’s assigned pay role is not ACTIVE. Activate the pay role or reassign an active role before payroll.',
  },
  AGREED_RATE_REQUIRED: {
    title: 'Provider-agreed rate not recorded',
    detail: 'Record a positive provider-agreed hourly rate on the classification linked for today.',
  },
  EMPLOYEE_CATEGORY_REQUIRED: {
    title: 'Worker payroll category not confirmed',
    detail:
      'Confirm the worker category (for example dayworker or shiftworker) on the worker payroll record.',
  },
  AWARD_REVIEW_REQUIRED: {
    title: 'Award review still required',
    detail:
      'The pay setup is recorded, but a material classification change still needs award review sign-off.',
  },
  PAY_ROLE_LINKS_INCOMPATIBLE: {
    title: 'Payroll evidence links do not match',
    detail:
      'The current role, profile and classification links are not compatible for today. Review the worker pay setup.',
  },
  EMPLOYMENT_TYPE_REQUIRED: {
    title: 'Employment type not set',
    detail: 'Set employment type (full-time, part-time, or casual) on the worker pay profile.',
  },
  SALARY_FLAT_CONFIG_INCOMPLETE: {
    title: 'Annualised salary configuration incomplete',
    detail:
      'Set annualised salary and pay cadence (supported frequency and matching periods per year) for this salary-flat worker.',
  },
  SALARY_FLAT_REVIEW_REQUIRED: {
    title: 'Annualised salary review required',
    detail:
      'Complete the annualised-salary reconciliation review for this salary-flat worker before payroll can resolve pay context.',
  },
  AGED_CARE_DC_L4_UNRESOLVED: {
    title: 'Direct-care Level 4 path not recorded',
    detail:
      'Aged Care direct-care L4 is either a Schedule I.1.1 translation or four years at L3 after 1 January 2025 (unreachable before 2029). Record which path applies. Do not count pre-2025 service.',
  },
};

function pushGap(gaps: PayrollSetupGap[], code: PayrollSetupGapCode): void {
  if (gaps.some((g) => g.code === code)) return;
  const entry = GAP_CATALOGUE[code];
  gaps.push(gap(code, entry.title, entry.detail));
}

/**
 * Detect worker payroll-setup completeness for an effective date.
 *
 * Award path (default): employment type, exactly one active pay profile, and —
 * on the LEGACY profile path (no active pay-role assignment) — classification
 * stream+level, pay point when stream/level present, and validated linkage to
 * the active profile. On the PAY-ROLE path the resolver sources paid
 * classification from the linked tenant_pay_roles row, so independent
 * classification is NOT required; instead an active+eligible assignment with an
 * ACTIVE tenant pay role is required (mirrors pay-role HARD blocks).
 *
 * Salary-flat path: salary-flat config, optional review-required gap, and
 * employment type when missing.
 * Agency / volunteer: treated complete (not award pay-context setup).
 */
export function workerPayrollSetupStatus(input: WorkerPayrollSetupInput): WorkerPayrollSetupStatus {
  const gaps: PayrollSetupGap[] = [];
  const paymentMethod = normalisedPaymentMethod(input.paymentMethod);

  if (isNonAwardPayrollPath(paymentMethod)) {
    return { complete: true, gaps: [] };
  }

  if (paymentMethod === 'CONTRACT_HOURLY') {
    const rate =
      input.agreedHourlyRate == null || input.agreedHourlyRate === ''
        ? null
        : Number(input.agreedHourlyRate);
    if (rate == null || !Number.isFinite(rate) || rate <= 0) {
      pushGap(gaps, 'AGREED_RATE_REQUIRED');
    }
    if (!hasText(input.employmentType)) {
      pushGap(gaps, 'EMPLOYMENT_TYPE_REQUIRED');
    }
    return { complete: gaps.length === 0, gaps };
  }

  if (isSalaryFlatMethod(paymentMethod)) {
    if (!isSalaryFlatConfigComplete(input.salaryFlat)) {
      pushGap(gaps, 'SALARY_FLAT_CONFIG_INCOMPLETE');
    }
    if (input.salaryFlatReviewRequired === true) {
      pushGap(gaps, 'SALARY_FLAT_REVIEW_REQUIRED');
    }
    if (!hasText(input.employmentType)) {
      pushGap(gaps, 'EMPLOYMENT_TYPE_REQUIRED');
    }
    return { complete: gaps.length === 0, gaps };
  }

  // ── Award-centred path ──────────────────────────────────────────────
  if (!hasText(input.employmentType)) {
    pushGap(gaps, 'EMPLOYMENT_TYPE_REQUIRED');
  }

  const rate =
    input.agreedHourlyRate == null || input.agreedHourlyRate === ''
      ? null
      : Number(input.agreedHourlyRate);
  if (rate == null || !Number.isFinite(rate) || rate <= 0) {
    pushGap(gaps, 'AGREED_RATE_REQUIRED');
  }
  const employeeCategory = String(input.employeeCategory ?? '')
    .trim()
    .toUpperCase();
  if (
    !['DAYWORKER', 'SHIFTWORKER', 'PERMANENT_NIGHT', 'ROTATING_ROSTER', 'OTHER'].includes(
      employeeCategory,
    )
  ) {
    pushGap(gaps, 'EMPLOYEE_CATEGORY_REQUIRED');
  }
  if (input.needsAwardReview === true) {
    pushGap(gaps, 'AWARD_REVIEW_REQUIRED');
  }
  if (input.payRoleLinksCompatible === false) {
    pushGap(gaps, 'PAY_ROLE_LINKS_INCOMPATIBLE');
  }

  const onPayRolePath = input.hasActivePayRoleAssignment === true;

  const hasStream = hasText(input.classificationStream);
  const hasLevel = hasText(input.classificationLevel);
  // Independent staff_classification_history stream/level/pay_point is only a
  // HARD requirement on the LEGACY profile path. On the pay-role path the
  // resolver sources paid classification (stream + level) from the linked
  // tenant_pay_roles row (pay-context-resolver.ts basePaidClassification), so an
  // absent staff_classification_history row does NOT hard-block — requiring it
  // here would falsely report the worker incomplete (Codex P1.5 P2 false-
  // incomplete). Rate resolution for the pay role is disclosed by the advisory
  // copy as still checked per shift.
  if (!onPayRolePath) {
    if (!hasStream || !hasLevel) {
      pushGap(gaps, 'STAFF_CLASSIFICATION_NOT_FOUND');
    } else if (!hasText(input.payPoint)) {
      // Pay point is optional on some awards (null rate lines exist) but is a
      // common new-worker gap that produces RATE_NOT_FOUND downstream. Surface
      // as setup incomplete when stream+level exist without a pay point.
      pushGap(gaps, 'PAY_POINT_MISSING');
    }
  }

  if (!input.hasActivePayProfile) {
    pushGap(gaps, 'STAFF_PAY_PROFILE_NOT_FOUND');
  }

  // Legacy profile path: resolver loads the classification linked by the
  // single active profile. If linkage is known false, treat as missing
  // classification for setup (would HARD-block at resolve time).
  // If linkage is unknown (null) and we are on the legacy path (no pay-role
  // assignment), do not allow "complete" solely on an independently selected
  // newest classification — surface classification gap to avoid false-positive
  // complete.
  const onLegacyProfilePath = !input.hasActivePayRoleAssignment;
  if (onLegacyProfilePath && input.hasActivePayProfile) {
    if (input.classificationLinkedToActiveProfile === false) {
      pushGap(gaps, 'STAFF_CLASSIFICATION_NOT_FOUND');
    } else if (input.classificationLinkedToActiveProfile == null && hasStream && hasLevel) {
      // Facts not fully knowable — prefer uncertainty over false complete.
      // Callers should prefer UNKNOWN UI when linkage cannot be loaded;
      // if they still call the detector, withhold complete.
      pushGap(gaps, 'STAFF_CLASSIFICATION_NOT_FOUND');
    }
  }

  // When the worker has any active pay-role assignment, the resolver takes
  // the pay-role path (legacy profile-only resolve is skipped). Setup must
  // then include an eligible assignment + active tenant role.
  if (input.hasActivePayRoleAssignment) {
    if (input.assignedPayRoleIsActive === false) {
      pushGap(gaps, 'PAY_ROLE_NOT_ACTIVE');
    }
    if (!input.hasActiveEligiblePayRoleAssignment) {
      // Distinguish "no assignment usable" vs "role required on event":
      // at worker level both collapse to missing eligible assignment setup.
      pushGap(gaps, 'PAY_ROLE_ASSIGNMENT_REQUIRED');
      pushGap(gaps, 'PAY_ROLE_REQUIRED');
    }
  }

  const l4Unresolved = unresolvedClassificationHistoricalInputs({
    awardCode: input.awardCode,
    classificationStream: input.classificationStream,
    classificationLevel: input.classificationLevel,
    asOfDate: input.asOfDate,
    agedCareDcL4Path: input.agedCareDcL4Path,
    agedCareL3ClassifiedFrom: input.agedCareL3ClassifiedFrom,
  }).some((item) => item.kind === 'AGED_CARE_DC_L4_SERVICE');
  if (l4Unresolved) {
    pushGap(gaps, 'AGED_CARE_DC_L4_UNRESOLVED');
  }

  return { complete: gaps.length === 0, gaps };
}
